import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { POWER_GAMMA_DEFAULT } from "../constants/chargerPower";

type PowerGammaContextValue = {
  gamma: number;
  setGamma: (value: number) => void;
};

const PowerGammaContext = createContext<PowerGammaContextValue | null>(null);

export function PowerGammaProvider({ children }: { children: ReactNode }) {
  const [gamma, setGammaState] = useState(POWER_GAMMA_DEFAULT);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/api/power/gamma", { credentials: "same-origin" });
        if (!res.ok || cancelled) return;
        const body = (await res.json()) as { gamma?: number };
        if (Number.isFinite(body.gamma)) setGammaState(body.gamma!);
      } catch {
        /* keep default */
      }
    })();

    const es = new EventSource("/api/power/gamma/live");
    es.addEventListener("gamma", (ev) => {
      try {
        const body = JSON.parse((ev as MessageEvent).data) as { gamma?: number };
        if (Number.isFinite(body.gamma)) setGammaState(body.gamma!);
      } catch {
        /* ignore */
      }
    });

    return () => {
      cancelled = true;
      es.close();
    };
  }, []);

  const persistGamma = useCallback((value: number) => {
    void fetch("/api/power/gamma", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ gamma: value }),
    }).catch(() => {
      /* SSE from server still syncs other clients; retry on next drag */
    });
  }, []);

  const setGamma = useCallback(
    (value: number) => {
      const clamped = Math.min(2, Math.max(0, value));
      setGammaState(clamped);
      if (persistTimer.current) clearTimeout(persistTimer.current);
      persistTimer.current = setTimeout(() => persistGamma(clamped), 120);
    },
    [persistGamma],
  );

  useEffect(
    () => () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    },
    [],
  );

  const value = useMemo(() => ({ gamma, setGamma }), [gamma, setGamma]);

  return <PowerGammaContext.Provider value={value}>{children}</PowerGammaContext.Provider>;
}

export function usePowerGamma(): PowerGammaContextValue {
  const ctx = useContext(PowerGammaContext);
  if (!ctx) {
    throw new Error("usePowerGamma must be used within PowerGammaProvider");
  }
  return ctx;
}
